import React, { useState, useEffect } from 'react';
import { RefreshCw, MessageSquare, Users, Database, Cpu } from 'lucide-react';
import { Button } from '@/components/ui/button';
import KBMaintenance from './KBMaintenance';
import { useDesign } from '@/contexts/DesignContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function StatCard({ icon: Icon, label, value, sub, testId }) {
  const { isStudio } = useDesign();
  if (isStudio) {
    return (
      <div data-testid={testId} className="st31-htile">
        <div className="st31-l"><Icon /> {label}</div>
        <div className="st31-v">{value}</div>
        {sub && <div className="st31-s">{sub}</div>}
      </div>
    );
  }
  return (
    <div data-testid={testId} className="bg-card border border-slate-200 dark:border-white/10 rounded-xl p-4">
      <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400 text-xs font-medium mb-1">
        <Icon className="w-3.5 h-3.5" />
        {label}
      </div>
      <div className="text-2xl font-bold text-slate-900 dark:text-white">{value}</div>
      {sub && <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
}

function UsageTable({ title, headers, rows, testId, small, num = [] }) {
  const { isStudio } = useDesign();
  if (isStudio) {
    return (
      <div data-testid={testId} className="st31-card">
        <div className="st31-ch"><h3 className="st31-ct">{title}{small && <small>{small}</small>}</h3></div>
        <div style={{ overflowX: 'auto' }}>
          <table className="st31-utbl">
            <thead>
              <tr>{headers.map((h, i) => <th key={h} className={num.includes(i) ? 'st31-n' : undefined}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={headers.length} style={{ textAlign: 'center', padding: '20px 14px' }}>No data yet</td></tr>
              )}
              {rows.map((cells, i) => (
                <tr key={i}>{cells.map((c, j) => <td key={j} className={num.includes(j) ? 'st31-n' : undefined}>{c}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }
  return (
    <div data-testid={testId} className="bg-card border border-slate-200 dark:border-white/10 rounded-xl overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-100 dark:border-white/10 font-semibold text-sm text-slate-900 dark:text-white">{title}</div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-slate-500 dark:text-slate-400 border-b border-slate-100 dark:border-white/10">
              {headers.map((h) => <th key={h} className="px-4 py-2 font-medium">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={headers.length} className="px-4 py-6 text-center text-slate-400 text-xs">No data yet</td></tr>
            )}
            {rows.map((cells, i) => (
              <tr key={i} className="border-b border-slate-50 dark:border-white/5 last:border-0">
                {cells.map((c, j) => <td key={j} className="px-4 py-2 text-slate-700 dark:text-slate-300">{c}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '');

export default function AIInsights({ adminQS }) {
  const { isStudio } = useDesign();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/ai/insights?${adminQS}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
    } catch (e) {
      setError(`Failed to load insights (${e.message})`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Classic draws these two states as bare text with no card frame (114 §14.21);
  // Studio gives them the panel's own skeleton and the one bad notice.
  if (loading) return isStudio ? (
    <div className="st31-stack" data-testid="ai-insights-loading">
      <div className="st31-g4">
        {[0, 1, 2, 3].map(i => (
          <div key={i} className="st31-htile">
            <span className="st31-sk" style={{ width: 96, height: 11 }} />
            <span className="st31-sk" style={{ width: 64, height: 20, marginTop: 6 }} />
            <span className="st31-sk" style={{ width: 112, height: 10, marginTop: 4 }} />
          </div>
        ))}
      </div>
      <span className="st31-sk" style={{ height: 13, width: '38%' }} />
    </div>
  ) : <div className="p-8 text-center text-slate-400 text-sm" data-testid="ai-insights-loading">Loading AI insights...</div>;
  if (error || !data) return isStudio ? (
    <div data-testid="ai-insights-error">
      <div className="st-notice st-notice-bad">
        <MessageSquare />
        <div><b>Couldn&rsquo;t load the AI insights.</b> {error || 'No data'} <button type="button" className="st-lnk" onClick={load}>Retry</button></div>
      </div>
    </div>
  ) : (
    <div className="p-8 text-center" data-testid="ai-insights-error">
      <p className="text-sm text-red-500 mb-3">{error || 'No data'}</p>
      <Button size="sm" variant="outline" onClick={load}>Retry</Button>
    </div>
  );

  const { overview, kb, system, church_usage, top_users, recent_conversations } = data;

  return (
    <div className={isStudio ? 'st31-stack' : 'space-y-4'} data-testid="ai-insights-panel">
      <div className="flex items-center justify-between">
        <p className={isStudio ? 'st31-cap' : 'text-xs text-slate-500 dark:text-slate-400'}>Usage numbers cover the last 30 days.</p>
        {isStudio ? (
          <button type="button" className="st-btn st-ghost st-sm" onClick={load} data-testid="ai-insights-refresh"><RefreshCw /> Refresh</button>
        ) : (
        <Button size="sm" variant="outline" onClick={load} data-testid="ai-insights-refresh">
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
        </Button>
        )}
      </div>

      <div className={isStudio ? 'st31-g4' : 'grid grid-cols-2 lg:grid-cols-4 gap-3'}>
        <StatCard icon={MessageSquare} label={isStudio ? 'AI chats (30d)' : 'AI Chats (30d)'} value={overview.chats_30d}
          sub={`${overview.conversations_30d} active conversations`} testId="stat-chats-30d" />
        <StatCard icon={Users} label={isStudio ? 'Active users (30d)' : 'Active Users (30d)'} value={overview.active_users_30d}
          sub={`${overview.conversations_total} conversations total`} testId="stat-active-users" />
        <StatCard icon={Database} label={isStudio ? 'Knowledge base' : 'Knowledge Base'} value={`${kb.active_articles} articles`}
          sub={`${kb.chunks} chunks · ${kb.index_vectors} vectors ${kb.index_ready ? '· ready' : '· loading'}`} testId="stat-kb" />
        <StatCard icon={Cpu} label={isStudio ? 'Server' : 'Server Health'} value={system.memory_mb ? `${system.memory_mb} MB` : 'n/a'}
          sub={`cache ${system.query_cache_entries}/${system.query_cache_max} · ${kb.answer_model}`} testId="stat-server" />
      </div>

      <UsageTable
        testId="church-usage-table"
        num={[3, 4]}
        title={isStudio ? 'Usage by church' : "Usage by Church (this month's credits)"}
        small={isStudio ? "credits this month" : undefined}
        headers={isStudio ? ['Church', 'Tier', 'Status', 'Credits used', 'Purchased extra'] : ['Church', 'Tier', 'Status', 'Credits Used', 'Purchased Extra']}
        rows={church_usage.map((c) => [
          c.name, c.tier, c.status,
          `${c.monthly_used} / ${c.monthly_limit}`,
          c.purchased || 0,
        ])}
      />

      <UsageTable
        testId="top-users-table"
        num={[1, 2]}
        title={isStudio ? 'Top users' : 'Top Users (chats in last 30 days)'}
        small={isStudio ? 'chats in the last 30 days' : undefined}
        headers={isStudio ? ['User', 'Chats', 'Conversations', 'Last active'] : ['User', 'Chats', 'Conversations', 'Last Active']}
        rows={top_users.map((u) => [
          u.name || u.email || String(u.user_id || '').slice(0, 8),
          u.chats_30d, u.conversations, fmtDate(u.last_active),
        ])}
      />

      <UsageTable
        testId="recent-conversations-table"
        num={[2]}
        title={isStudio ? 'Recent conversations' : 'Recent Conversations'}
        headers={['Title', 'User', 'Messages', 'Updated']}
        rows={recent_conversations.map((c) => [
          c.title, c.user, c.messages, fmtDate(c.updated_at),
        ])}
      />

      <KBMaintenance adminQS={adminQS} />
    </div>
  );
}
